import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Avatar
} from '@mui/material';
import { connect } from 'react-redux';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import GlobalSearch from '../components/GlobalSearch';
import { GeoTabDevice, GeotabDeviceStatusInfoRes } from '../models/GeotabRes';
import AppConstants from '../constants/AppConstants';
import { hide, show } from '../store/actions/busyIndicatorActions';
import { formatErrorMessage } from '../utils/common';
import HttpQueriesService from '../services/HttpQueriesService';

// credentials
const MAPBOX_TOKEN = AppConstants.MAPBOX_KEY;

// The following is required to stop "npm build" from transpiling mapbox code.
// notice the exclamation point in the import.
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

interface Vehicle {
  id: string;
  name: string;
  lat: number;
  lng: number;
  deviceType?: string;
  serialNumber?: string;
  comment?: string;
  isActive?: boolean;
  groups?: Array<{ id: string }>;
}

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any
}

const LiveGeotab: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [devices, setDevices] = useState<GeoTabDevice[]>([]);
  const [searchedText, setSearchedText] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const fetchDeviceInfo = async (deviceIds: string[]) => {
    HttpQueriesService.geotabDevice(deviceIds)
      .then((resp: any) => {
        setDevices(resp);
        // Merge device info with vehicles
        setVehicles((prev) => prev.map((v) => {
          const device = resp.find((d: any) => d.id === v.id);
          return device ? { ...v, ...device } : v;
        }));
        // hideBusyIndicator();
        setLoading(false);
      }).catch((err: any) => {
        // hideBusyIndicator();
        setLoading(false);
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const fetchVehicleLocations = () => {
    HttpQueriesService.geotabDeviceStatusInfo()
      .then((resp: any) => {
        const filtered = resp
          .filter((p: any) => p.latitude && p.longitude)
          .map((p: any) => ({
            id: p.device.id,
            name: p.driver || 'Unknown Driver',
            lat: p.latitude,
            lng: p.longitude
          }));

        setVehicles(filtered);

        // Fetch additional device info
        const deviceIds = filtered.map((v: Vehicle) => v.id);
        if (deviceIds.length > 0) {
          fetchDeviceInfo(deviceIds);
        }
        // hideBusyIndicator();
        // setLoading(false);
      }).catch((err: any) => {
        // hideBusyIndicator();
        setLoading(false);
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // await showBusyIndicator();
        fetchVehicleLocations();
      } catch (err) {
        console.error('Failed to fetch vehicle data:', err);
      } finally {
        // setLoading(false);
        // hideBusyIndicator();
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (vehicles.length === 0 || !mapContainerRef.current) return;

    // Initialize map
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [vehicles[0].lng || 0, vehicles[0].lat || 0],
      zoom: 12,
      accessToken: MAPBOX_TOKEN
    });
    mapRef.current = map;

    // Add controls
    map.addControl(new mapboxgl.NavigationControl());

    // Create a popup but don't add it to the map yet
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    // Add markers with enhanced info
    vehicles.forEach((vehicle) => {
      // Create a marker element
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.backgroundImage = 'url(https://docs.mapbox.com/mapbox-gl-js/assets/custom_marker.png)';
      el.style.width = '30px';
      el.style.height = '38px';
      el.style.backgroundSize = '100%';
      el.style.backgroundRepeat = 'no-repeat';

      // Create marker
      new mapboxgl.Marker(el)
        .setLngLat([vehicle.lng || 0, vehicle.lat || 0])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div>
                <h3>${vehicle.name}</h3>
                <p><strong>Device:</strong> ${vehicle.deviceType || 'Unknown'}</p>
                <p><strong>Serial:</strong> ${vehicle.serialNumber || 'Unknown'}</p>
                ${vehicle.comment ? `<p><em>${vehicle.comment.substring(0, 50)}...</em></p>` : ''}
              </div>
            `)
        )
        .addTo(map);
    });
  }, [vehicles]);

  const handleVehicleSelect = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [vehicle.lng, vehicle.lat],
        zoom: 15
      });
    }
  };

  const onSearchTextChange = (event: any) => {
    setSearchedText(event.target.value);
  };

  return (
    <>
      <Helmet>
        <title>Live Vehicle Tracking</title>
      </Helmet>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100%'
        }}
      >
        {/* Sidebar */}
        <Paper
          sx={{
            display: { xs: 'none', md: 'block' },
            width: 350,
            height: '100%',
            overflow: 'auto',
            p: 2,
            borderRadius: 0
          }}
          elevation={3}
        >
          {/* <Typography variant="h6" gutterBottom>
            Vehicle List
          </Typography>
          <Divider sx={{ mb: 2 }} /> */}
          <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn={false} width="100%" />
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {vehicles.filter((vehicle) => vehicle.name.toLowerCase().includes(searchedText.toLowerCase())).map((vehicle) => (
                <div key={vehicle.id}>
                  <ListItem
                    button
                    onClick={() => handleVehicleSelect(vehicle)}
                    selected={selectedVehicle?.id === vehicle.id}
                  >
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      {vehicle.name.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={vehicle.name}
                      secondary={(
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {vehicle.deviceType || 'Unknown device'}
                          </Typography>
                          {vehicle.serialNumber && ` • ${vehicle.serialNumber}`}
                        </>
                      )}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </div>
              ))}
            </List>
          )}
        </Paper>

        {/* Map */}
        <Box
          sx={{
            flexGrow: 1,
            position: 'relative'
          }}
        >
          {selectedVehicle && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 1,
                backgroundColor: 'white',
                p: 1,
                borderRadius: 1,
                boxShadow: 3
              }}
            >
              <Chip
                label="Geotab"
                color="primary"
                size="medium"
              />
              <Chip
                label={`${selectedVehicle.name} (${selectedVehicle.deviceType || 'Unknown'})`}
                color="primary"
                size="medium"
              />
            </Box>
          )}
          <Box
            ref={mapContainerRef}
            sx={{
              height: '100%',
              width: '100%'
            }}
          />
        </Box>
      </Box>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts()),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
});
export default connect(null, mapDispatchToProps)(LiveGeotab);
